/**
 * Carrossel "Destaques" — 2ª ilha do delivery V2.
 *
 * Substitui o bloco `promosHtml` do V1 (delivery_app.html ~2861-2950).
 *
 * Regra de prioridade (paridade V1):
 *  1. Produtos com is_highlighted=true (recomendados manualmente)
 *  2. Produtos com is_promo_active=true (em promoção)
 *  3. Fallback: produtos com imagem (até 8) se 1 e 2 vazios
 *
 * UX (padrão Instagram Reels carousel / iFood destaques):
 *  - Scroll horizontal nativo com snap-x mandatory
 *  - 1 card por viewport no mobile (~70% largura)
 *  - 2-3 cards desktop
 *  - Setas só no desktop (hover)
 *  - Skeleton enquanto MENU_DATA não chega
 *  - Click → delega pro wizard V1 (`window.openProductDetail`)
 *
 * Performance:
 *  - lazy loading nas imagens
 *  - skeleton bg-pulse enquanto img carrega
 *  - sem GPU pesado (no backdrop-blur, no shadow exagerado)
 *  - 1 mount React (não 1 por card)
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { t, tr, useI18n } from '../i18n';
import type { MenuCategory, MenuProduct } from '../types';

declare global {
  interface Window {
    // MENU_DATA vem do global.d.ts (union de Array delivery + Object admin).
    openProductDetail?: (productId: number) => void;
  }
}

// V1 marca produtos com `is_promo_active` (não está em MenuProduct types
// porque é flag temporária do backend baseada em promoção ativa).
type HighlightProduct = MenuProduct & { is_promo_active?: boolean };

function pickFeatured(menu: MenuCategory[]): HighlightProduct[] {
  const out: HighlightProduct[] = [];
  const added = new Set<number>();

  // 1) is_highlighted (manual — prioridade máxima)
  for (const cat of menu) {
    for (const p of (cat.items || []) as HighlightProduct[]) {
      if (p.is_hidden) continue;
      if (p.is_highlighted && !added.has(p.id)) {
        out.push(p);
        added.add(p.id);
      }
    }
  }

  // 2) is_promo_active
  for (const cat of menu) {
    for (const p of (cat.items || []) as HighlightProduct[]) {
      if (p.is_hidden) continue;
      if (p.is_promo_active && !added.has(p.id)) {
        out.push(p);
        added.add(p.id);
      }
    }
  }

  // 3) Fallback: produtos com foto (até 8) — só se 1 e 2 vazios
  if (out.length === 0) {
    for (const cat of menu) {
      for (const p of (cat.items || []) as HighlightProduct[]) {
        if (p.is_hidden) continue;
        if (p.image && out.length < 8 && !added.has(p.id)) {
          out.push(p);
          added.add(p.id);
        }
      }
    }
  }

  return out;
}

function fmtPriceBR(n: number): string {
  return n.toFixed(2).replace('.', ',');
}

function PriceBlock({ product }: { product: HighlightProduct }) {
  const price = Number(product.price) || 0;
  const original = Number(product.original_price) || 0;
  const isInPromo = original > price && price > 0;
  const hasSizes = !!product.size_prices && Object.keys(product.size_prices).length > 0;

  if (isInPromo) {
    return (
      <div className="flex items-baseline gap-1.5 w-full overflow-hidden">
        <span className="text-base font-extrabold text-gray-900 truncate">
          R$ {fmtPriceBR(price)}
        </span>
        <span className="text-[11px] font-medium text-gray-400 line-through decoration-gray-300 truncate">
          R$ {fmtPriceBR(original)}
        </span>
      </div>
    );
  }

  if (price === 0) {
    return (
      <div className="flex flex-col w-full overflow-hidden mt-auto">
        <span className="text-[10px] font-bold text-orange-600 uppercase tracking-widest bg-orange-50 px-2 py-1 rounded w-max border border-orange-200">
          <i className="fas fa-sliders-h mr-1" /> {t('A Escolher')}
        </span>
      </div>
    );
  }

  const prefix = product.is_pizza || hasSizes ? t('A partir de ') : '';
  return (
    <div className="flex flex-col w-full overflow-hidden">
      {prefix && (
        <span className="text-[10px] text-gray-500 font-medium truncate mb-0.5">
          {prefix}
        </span>
      )}
      <span className="text-base font-extrabold text-gray-900 truncate">
        R$ {fmtPriceBR(price)}
      </span>
    </div>
  );
}

function HighlightCard({ product }: { product: HighlightProduct }) {
  const [imgLoaded, setImgLoaded] = useState(false);
  const imgSrc = product.image || '';

  const onClick = () => {
    if (typeof window.openProductDetail === 'function') {
      window.openProductDetail(product.id);
    }
  };

  return (
    <div
      onClick={onClick}
      className="snap-start shrink-0 bg-white rounded-2xl border border-gray-100 shadow-[0_4px_12px_rgba(0,0,0,0.04)] overflow-hidden transition active:scale-[0.98] cursor-pointer flex flex-col group relative"
      style={{ width: 185, minWidth: 185, maxWidth: 185, WebkitTapHighlightColor: 'transparent' }}
    >
      <div className="relative w-full bg-gray-50 overflow-hidden" style={{ height: 140 }}>
        <div className="absolute top-2.5 left-2.5 z-20 flex flex-col gap-1 items-start">
          {product.is_highlighted && (
            <div className="bg-yellow-400 text-yellow-900 text-[10px] font-extrabold px-2.5 py-0.5 rounded-full shadow-sm flex items-center gap-1 tracking-wide">
              <i className="fas fa-star text-[9px]" /> {t('Recomendado')}
            </div>
          )}
          {product.is_promo_active && (
            <div className="bg-white/95 border border-white/20 text-red-600 text-[10px] font-extrabold px-2.5 py-0.5 rounded-full shadow-sm flex items-center gap-1 tracking-wide">
              <i className="fas fa-tag text-[9px]" /> {t('Oferta')}
            </div>
          )}
        </div>

        {!imgLoaded && <div className="absolute inset-0 bg-gray-200 animate-pulse z-10" />}

        {imgSrc ? (
          <img
            src={imgSrc}
            loading="lazy"
            alt={tr(product.name)}
            className="w-full h-full object-cover transition-transform duration-300 group-active:scale-105"
            onLoad={() => setImgLoaded(true)}
            onError={(e) => {
              const img = e.target as HTMLImageElement;
              img.src = 'https://placehold.co/400x300?text=Sem+Foto';
              setImgLoaded(true);
            }}
            draggable={false}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300">
            <i className="fas fa-camera text-3xl opacity-40" />
          </div>
        )}

        {/* Gradient bottom — melhora contraste se loja usar logo claro como foto */}
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/20 to-transparent pointer-events-none z-10" />
      </div>

      <div className="p-3 flex flex-col flex-1 bg-white z-20">
        <h4 className="text-sm font-semibold text-gray-800 leading-snug mb-2 line-clamp-2" style={{ minHeight: 40 }}>
          {tr(product.name)}
        </h4>
        <div className="mt-auto">
          <PriceBlock product={product} />
        </div>
      </div>
    </div>
  );
}

export function HighlightsCarousel() {
  // assina a troca de idioma (ver i18n.ts)
  useI18n();
  const { t } = useI18n();
  // MENU_DATA pode mudar (V1 atualiza após fetch + after processPromotionalMenu).
  // Re-render quando dispara `aliv:home-rendered`.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const onHomeRendered = () => setTick((t) => t + 1);
    window.addEventListener('aliv:home-rendered', onHomeRendered);
    return () => window.removeEventListener('aliv:home-rendered', onHomeRendered);
  }, []);

  const products = useMemo(() => {
    const menu = (window.MENU_DATA as MenuCategory[] | undefined) || [];
    return pickFeatured(menu);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);

  const scrollerRef = useRef<HTMLDivElement>(null);
  const scrollBy = (dx: number) => {
    scrollerRef.current?.scrollBy({ left: dx, behavior: 'smooth' });
  };

  if (products.length === 0) return null;

  return (
    <div id="delivery-destaques" className="mb-6 mt-4 scroll-mt-32 lg:scroll-mt-20">
      <h3 className="font-black text-xl text-gray-800 mb-3 px-4 flex items-center gap-2">
        <i className="fas fa-star text-yellow-500" /> {t('Destaques')}
      </h3>
      <div className="relative group">
        <button
          type="button"
          aria-label={t('Anterior')}
          onClick={() => scrollBy(-400)}
          className="hidden md:flex absolute left-1 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-white shadow-lg items-center justify-center text-gray-700 hover:bg-gray-50 active:scale-95 transition opacity-0 group-hover:opacity-100"
        >
          <i className="fas fa-chevron-left text-sm" />
        </button>
        <div
          ref={scrollerRef}
          className="flex overflow-x-auto gap-3 px-4 hide-scroll snap-x snap-mandatory py-2 scroll-smooth"
        >
          {products.map((p) => (
            <HighlightCard key={p.id} product={p} />
          ))}
        </div>
        <button
          type="button"
          aria-label={t('Próximo')}
          onClick={() => scrollBy(400)}
          className="hidden md:flex absolute right-1 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-white shadow-lg items-center justify-center text-gray-700 hover:bg-gray-50 active:scale-95 transition opacity-0 group-hover:opacity-100"
        >
          <i className="fas fa-chevron-right text-sm" />
        </button>
      </div>
    </div>
  );
}
